import { parse } from '@conform-to/zod'
import { json, redirect } from '@remix-run/node'
import { prisma } from '~/utils/config/db.server.ts'
import { commitSession, getSession } from '../../utils/config/session.server.ts'
import { z } from 'zod'
import { type Submission } from '@conform-to/react'
import {
  getDomainUrl,
  invariant,
  invariantResponse,
} from '../../utils/misc.tsx'
import { generateTOTP, verifyTOTP } from '@epic-web/totp'
import { resetPasswordEmailSessionKey } from '~/routes/_auth+/reset-password.tsx'

// SIGNUP
export const singUpEmailSessionKey = 'singUpEmail'
export async function handleSingUpVerification({
  request,
  submission,
}: VerifyFunctionArgs) {
  invariant(submission.value, 'submission.value should be defined by now')
  const session = await getSession(request.headers.get('cookie'))
  session.set(singUpEmailSessionKey, submission.value.target)
  return redirect('/signup', {
    headers: { 'Set-Cookie': await commitSession(session) },
  })
}

// RESET PASSWORD
export async function handleForgotPasswordVerification({
  request,
  submission,
}: VerifyFunctionArgs) {
  invariant(submission.value, 'submission.value should be defined by now')
  const target = submission.value.target
  const user = await prisma.user.findFirst({
    where: { OR: [{ email: target }, { email: target }] },
    select: { email: true },
  })
  // we don't want to say the user is not found if the email is not found
  // because that would allow an attacker to check if an email is registered
  invariantResponse(user, 'Invalid code')

  const session = await getSession(request.headers.get('cookie'))
  session.set(resetPasswordEmailSessionKey, user.email)
  return redirect('/reset-password', {
    headers: { 'Set-Cookie': await commitSession(session) },
  })
}

// VERIFY
export const ROUTE_PATH = '/resources/verify'

export const codeQueryParam = 'code'
export const targetQueryParam = 'target'
export const typeQueryParam = 'type'
export const redirectToQueryParam = 'redirectTo'
const types = ['forgot-password', 'signup-form', 'change-email'] as const

export const VerifySchema = z.object({
  [codeQueryParam]: z.string().min(6).max(6),
  [typeQueryParam]: z.enum(types),
  [targetQueryParam]: z.string(),
  [redirectToQueryParam]: z.string().optional(),
})

export type VerificationTypes = (typeof types)[number]

export type VerifyFunctionArgs = {
  request: Request
  submission: Submission<z.infer<typeof VerifySchema>>
  body: FormData | URLSearchParams
}

export function getRedirectToUrl({
  request,
  type,
  target,
}: {
  request: Request
  type: VerificationTypes
  target: string
}) {
  const redirectToUrl = new URL(`${getDomainUrl(request)}/verify`)
  redirectToUrl.searchParams.set(typeQueryParam, type)
  redirectToUrl.searchParams.set(targetQueryParam, target)
  return redirectToUrl
}

export async function prepareVerification({
  period,
  request,
  type,
  target,
}: {
  period: number
  request: Request
  type: VerificationTypes
  target: string
}) {
  const verifyUrl = getRedirectToUrl({ request, type, target })
  const redirectTo = new URL(verifyUrl.toString())

  const { otp, charSet, ...verificationConfig } = generateTOTP({
    algorithm: 'SHA256',
    // Leaving off 0 and O on purpose to avoid confusing users.
    period,
  })
  const verificationData = {
    type,
    target,
    ...verificationConfig,
    expiresAt: new Date(Date.now() + verificationConfig.period * 1000),
  }
  console.log({ verificationConfig })
  await prisma.verification.upsert({
    where: { target_type: { target, type } },
    create: verificationData,
    update: verificationData,
  })

  // add the otp to the url we'll email the user.
  verifyUrl.searchParams.set(codeQueryParam, otp)

  return { otp, redirectTo, verifyUrl }
}

export type VerifySubmission = Submission<z.infer<typeof VerifySchema>>

export async function isCodeValid({
  code,
  type,
  target,
}: {
  code: string
  type: VerificationTypes
  target: string
}) {
  const verification = await prisma.verification.findUnique({
    where: {
      target_type: { target, type },
      OR: [{ expiresAt: { gt: new Date() } }, { expiresAt: null }],
    },
    select: { algorithm: true, secret: true, period: true },
  })
  if (!verification) return false
  const result = verifyTOTP({
    otp: code,
    period: verification.period,
    secret: verification.secret,
    algorithm: verification.algorithm,
  })
  if (!result) return false

  return true
}

export async function validateRequest(
  request: Request,
  body: URLSearchParams | FormData,
) {
  const submission = await parse(body, {
    schema: () =>
      VerifySchema.superRefine(async (data, ctx) => {
        const codeIsValid = await isCodeValid({
          code: data[codeQueryParam],
          type: data[typeQueryParam],
          target: data[targetQueryParam],
        })
        if (!codeIsValid) {
          ctx.addIssue({
            path: ['code'],
            code: z.ZodIssueCode.custom,
            message: `Invalid code`,
          })
          return
        }
      }),
    async: true,
  })

  if (submission.intent !== 'submit') {
    return json({ status: 'idle', submission } as const)
  }
  if (!submission.value) {
    return json({ status: 'error', submission } as const, { status: 400 })
  }

  const { value: submissionValue } = submission

  async function deleteVerification() {
    return prisma.verification.delete({
      where: {
        target_type: {
          type: submissionValue[typeQueryParam],
          target: submissionValue[targetQueryParam],
        },
      },
    })
  }

  switch (submissionValue[typeQueryParam]) {
    case 'forgot-password': {
      await deleteVerification()
      return handleForgotPasswordVerification({ request, body, submission })
    }
    case 'signup-form': {
      await deleteVerification()
      return handleSingUpVerification({ request, body, submission })
    }
    default: {
      submission.error[''] = ['Invalid verification type']
      return json({ status: 'error', submission } as const, { status: 400 })
    }
  }
}
